/* eslint-disable complexity */
/* eslint-disable max-lines */
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { XboxHandheldUrl, XboxPlayAnywhereUrl } from "../../constants";
import { GameConceptApplication } from "../../helpers/gameConceptHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import {
  adjustToLocalFromUTC,
  dateToISOString,
  finalBackendCompensation,
  safeToDate,
} from "../../server/crm/api/mappers/utils/dateUtils";
import { HubProgramDisplayStatusCode } from "../../server/crm/dataService/models/staticOptions";
import { FormattedText } from "../FormattedText/FormattedText";
import { Modal } from "../Modal/Modal";
import { LoadingSpinner } from "../Spinner/Spinner";
import * as styles from "./XpaModal.module.css";

type XpaModalProps = {
  isOpen: boolean;
  onClose: () => void;
  saveAndOptIn: (selectedDate: Date | null, isValidDate: boolean) => void;
  onSaveAndExit: (selectedDate: Date | null) => void;
  gameTitle: string;
  gameConcept: GameConceptApplication;
  isLoading?: boolean;
  errorMessage?: string;
};

const MAX_DEADLINE_VALIDATION_DATE = new Date(2027, 0, 1);

export const XpaModal = ({
  isOpen,
  onClose,
  saveAndOptIn,
  onSaveAndExit,
  gameTitle,
  gameConcept,
  isLoading = false,
  errorMessage: externalErrorMessage = "",
}: XpaModalProps) => {
  const [
    XPAModalRequerimentsDescription,
    XPAIncentiveEnrollmentTitle,
    XPAIncentiveEnrollmentSubtitle,
    XPAIncentiveEnrollmentSectionTitle,
    XPAIncentiveEnrollmentConfirmedReleaseDateTitle,
    XPAIncentiveEnrollmentConfirmedReleaseDateTitleUTC,
    XPASubmittedReleaseDate,
    XPAIncentiveReleaseDateValidationError,
    XPAIncentiveReleaseDateOutOfRangeError,
    XPAIncentiveReleaseDateValid,
    SaveOptIn,
    exit,
    saveAndExit,
    NoDateAvailable,
    ChooseConfirmedReleaseDateAndTime,
    XPALoadingText,
    applicationStatusDefaultGameTitle,
    XPAIncentiveError,
    XPAIncentiveProgressError,
  ] = useStrings(
    "XPAModalRequerimentsDescription",
    "XPAIncentiveEnrollmentTitle",
    "XPAIncentiveEnrollmentSubtitle",
    "XPAIncentiveEnrollmentSectionTitle",
    "XPAIncentiveEnrollmentConfirmedReleaseDateTitle",
    "XPAIncentiveEnrollmentConfirmedReleaseDateTitleUTC",
    "XPASubmittedReleaseDate",
    "XPAIncentiveReleaseDateValidationError",
    "XPAIncentiveReleaseDateOutOfRangeError",
    "XPAIncentiveReleaseDateValid",
    "SaveOptIn",
    "exit",
    "saveAndExit",
    "NoDateAvailable",
    "ChooseConfirmedReleaseDateAndTime",
    "XPALoadingText",
    "applicationStatusDefaultGameTitle",
    "XPAIncentiveError",
    "XPAIncentiveProgressError"
  );

  const [validationMessage, setValidationMessage] = useState<string>("");
  const [isDateValid, setIsDateValid] = useState(false);
  const [hasValidationAttempted, setHasValidationAttempted] = useState(false);
  const [localErrorMessage, setLocalErrorMessage] = useState<string>("");

  const programTracking = gameConcept.gameConceptInfo.state.programTracking;
  const gamingHubprogramtargetdateutc = programTracking?.gamingHubprogramtargetdateutc ?? null;
  const [confirmReleaseDate, setConfirmReleaseDate] = useState<Date | null>(
    adjustToLocalFromUTC(new Date(gamingHubprogramtargetdateutc ?? ""))
  );

  // Auto-validate release date after user stops changing it (debounced)
  useEffect(() => {
    if (confirmReleaseDate && !hasValidationAttempted) {
      const timer = setTimeout(() => {
        validateReleaseDate();
      }, 1000); // Wait 1 second after user stops changing the date

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [confirmReleaseDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine modal mode based on Hub Program Display Status
  const hubProgramDisplayStatus = programTracking?.gamingHubprogramdisplaystatus;
  const isReadOnlyMode =
    hubProgramDisplayStatus === HubProgramDisplayStatusCode.Enrolled ||
    hubProgramDisplayStatus === HubProgramDisplayStatusCode.UnderReview; // Enrolled or Under Review
  const isEditMode =
    hubProgramDisplayStatus === HubProgramDisplayStatusCode.Draft ||
    hubProgramDisplayStatus === null ||
    hubProgramDisplayStatus === undefined; // Draft, NULL, or undefined (when programTracking is null)

  const errorMessage = externalErrorMessage || localErrorMessage;

  const getPlatformReleaseDate = (platformName: string): string | undefined =>
    gameConcept.gameConceptInfo.gameExpectations?.targetPlatforms?.find(
      platform => platform.platform === platformName
    )?.releaseDate;

  const gameConceptData = {
    gamingXpapartnerconfirmedreleasedateutc: dateToISOString(gamingHubprogramtargetdateutc),
    ggpTargetreleasedate: gamingHubprogramtargetdateutc,
    ggpScarlettreleasedate: getPlatformReleaseDate("xboxSeriesXS"),
    gamingWindowsStoreReleaseDate: getPlatformReleaseDate("windowsStore"),
  };

  const getSubmittedReleaseDate = (): Date | null => {
    if (isReadOnlyMode) {
      return safeToDate(gameConceptData?.gamingXpapartnerconfirmedreleasedateutc);
    }

    const gameTargetDate = safeToDate(gameConceptData?.ggpTargetreleasedate);
    const xboxDate = safeToDate(gameConceptData?.ggpScarlettreleasedate);
    const windowsDate = safeToDate(gameConceptData?.gamingWindowsStoreReleaseDate);

    return gameTargetDate ?? xboxDate ?? windowsDate ?? null;
  };

  const submittedReleaseDate = getSubmittedReleaseDate();

  const handleSaveAndOptIn = () => {
    setLocalErrorMessage("");
    try {
      const finalDate = finalBackendCompensation(confirmReleaseDate);
      saveAndOptIn(finalDate, isDateValid);
    } catch (error) {
      setLocalErrorMessage(XPAIncentiveError);
    }
  };

  const handleExitAndSave = () => {
    setLocalErrorMessage("");
    try {
      const finalDate = finalBackendCompensation(confirmReleaseDate);
      onSaveAndExit(finalDate);
    } catch (error) {
      setLocalErrorMessage(XPAIncentiveProgressError);
    }
  };

  const handleClose = () => {
    setLocalErrorMessage("");
    onClose();
  };

  const validateReleaseDate = () => {
    if (!confirmReleaseDate) {
      setValidationMessage(XPAIncentiveReleaseDateValidationError);
      setIsDateValid(false);
      setHasValidationAttempted(false);
      return;
    }

    const today = new Date();
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    setHasValidationAttempted(true);

    if (
      confirmReleaseDate < thirtyDaysFromNow ||
      confirmReleaseDate >= MAX_DEADLINE_VALIDATION_DATE
    ) {
      setValidationMessage(XPAIncentiveReleaseDateOutOfRangeError);
      setIsDateValid(false);
    } else {
      setValidationMessage(XPAIncentiveReleaseDateValid);
      setIsDateValid(true);
    }
  };

  const XPAModalRequerimentsDescriptionFormatted = XPAModalRequerimentsDescription.replace(
    "{0}",
    XboxPlayAnywhereUrl
  ).replace("{1}", XboxHandheldUrl);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{XPAIncentiveEnrollmentTitle}</h2>
          {!isLoading || errorMessage ? (
            <button className={styles.closeButton} onClick={onClose} aria-label="Close modal">
              ×
            </button>
          ) : null}
        </div>

        <div className={styles.modalBody}>
          {isLoading && !errorMessage ? (
            <div className={styles.loadingOverlay}>
              <LoadingSpinner />
              <div className={styles.loadingText}>{XPALoadingText}</div>
            </div>
          ) : null}

          {errorMessage && <div className={styles.errorMessageContainer}>{errorMessage}</div>}

          <div className={styles.description}>{XPAIncentiveEnrollmentSubtitle}</div>

          <Row>
            <Column className={styles.leftSection}>
              <h3 className={styles.sectionTitle}>{XPAIncentiveEnrollmentSectionTitle}</h3>
              <div className={styles.requirementsContainer}>
                <FormattedText
                  className={styles.requirementsDescription}
                  text={XPAModalRequerimentsDescriptionFormatted}
                ></FormattedText>
              </div>
            </Column>

            <Column className={styles.rightSection}>
              <h3 className={styles.gameTitle}>{gameTitle || applicationStatusDefaultGameTitle}</h3>

              {isReadOnlyMode ? (
                // Read-only mode for Enrolled status
                <div className={styles.readOnlyDateContainer}>
                  <h4 className={`${styles.sectionTitle} ${styles.readOnlyTitleMargin}`}>
                    {XPAIncentiveEnrollmentConfirmedReleaseDateTitle}
                  </h4>
                  <div
                    className={`${styles.datePickerContainer} ${styles.readOnlyDatePickerContainer}`}
                  >
                    <DatePicker
                      selected={submittedReleaseDate}
                      onChange={() => {}} // Read-only
                      className={`${styles.datePicker} ${styles.readOnlyDatePicker}`}
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="MM/dd/yyyy"
                      disabled={true}
                      placeholderText={submittedReleaseDate ? "" : NoDateAvailable}
                      data-testid="readonly-date-picker"
                    />
                  </div>
                </div>
              ) : isEditMode ? (
                // Edit mode for Draft or NULL status
                <>
                  <h4 className={styles.sectionTitle}>{XPASubmittedReleaseDate}</h4>
                  <div className={styles.datePickerContainer}>
                    <DatePicker
                      selected={submittedReleaseDate}
                      onChange={() => {}} // Read-only
                      className={`${styles.datePicker} ${styles.readOnlyDatePicker}`}
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="MM/dd/yyyy"
                      disabled={true}
                      placeholderText={submittedReleaseDate ? "" : NoDateAvailable}
                      data-testid="submitted-date-picker"
                    />
                  </div>

                  <h4 className={styles.sectionTitle}>
                    {XPAIncentiveEnrollmentConfirmedReleaseDateTitleUTC}
                    <span className={styles.requiredAsterisk}>*</span>
                  </h4>
                  <div className={styles.datePickerContainer}>
                    <DatePicker
                      selected={confirmReleaseDate}
                      onChange={(date: Date | null) => {
                        setConfirmReleaseDate(date);
                        setValidationMessage("");
                        setIsDateValid(false);
                        setHasValidationAttempted(false);
                      }}
                      onCalendarClose={() => {
                        if (confirmReleaseDate) {
                          validateReleaseDate();
                        }
                      }}
                      placeholderText={
                        submittedReleaseDate ? "" : ChooseConfirmedReleaseDateAndTime
                      }
                      className={styles.datePicker}
                      minDate={new Date()}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="MM/dd/yyyy h:mm aa"
                      data-testid="confirm-date-picker"
                    />
                  </div>
                  {validationMessage && (
                    <div
                      className={`${styles.validationMessage} ${
                        isDateValid ? styles.validMessage : styles.errorMessage
                      }`}
                    >
                      {validationMessage}
                    </div>
                  )}

                  <div className={styles.buttonContainer}>
                    <BasicButton
                      as="button"
                      buttonType={ButtonType.Primary}
                      onClick={handleSaveAndOptIn}
                      className={`${styles.acceptButton} ${styles.saveOptInButton}`}
                      disabled={!hasValidationAttempted}
                    >
                      {SaveOptIn}
                    </BasicButton>
                  </div>
                </>
              ) : null}
            </Column>
          </Row>

          <div className={styles.bottomButtons}>
            <BasicButton
              as="button"
              buttonType={ButtonType.Secondary}
              onClick={handleClose}
              className={styles.exitButton}
            >
              {exit}
            </BasicButton>
            {isEditMode && (
              <BasicButton
                as="button"
                buttonType={ButtonType.Secondary}
                onClick={handleExitAndSave}
                className={styles.exitSaveButton}
              >
                {saveAndExit}
              </BasicButton>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
